import type { Moment } from 'moment'
import type { DaysToGoEventKind } from 'checkout/Components/Confirmation/BookingDetailsV2/utils'

export interface UpcomingEscapePrimaryBooking {
  kind: DaysToGoEventKind
  startDate?: string | Moment
  endDate?: string | Moment
  duration?: number
  /** Hotel / bedbank offer id when the primary line is a stay. */
  stayOfferId?: string
  /** Experience offer id when the primary line is an experience. */
  experienceId?: string
  /** Tour reservation id used to fetch the tour snapshot hero image. */
  tourReservationId?: string
  /** Cruise booking id used to fetch the cruise snapshot hero image. */
  cruiseBookingId?: string
  imageId?: string
  stayName?: string
}

function isActiveItem(item: { status?: string }): boolean {
  return item.status !== 'cancelled'
}

/**
 * One product line for Upcoming Escape title, countdown, date range, and hero.
 * Stay (only with a check-in date) → tour → cruise → flight → experience.
 */
function getUpcomingEscapePrimaryBooking(order: App.Order): UpcomingEscapePrimaryBooking {
  const hotelItem = order.items.find(item => isActiveItem(item) && !!item.reservation?.startDate)
  if (hotelItem) {
    const stayName = hotelItem.offer?.property?.name || hotelItem.offer?.name
    return {
      kind: 'stay',
      startDate: hotelItem.reservation?.startDate,
      endDate: hotelItem.reservation?.endDate,
      duration: hotelItem.duration,
      ...(hotelItem.offerId ? { stayOfferId: hotelItem.offerId } : {}),
      ...(hotelItem.offer?.image?.id ? { imageId: hotelItem.offer.image.id } : {}),
      ...(stayName ? { stayName } : {}),
    }
  }

  const bedbankItem = order.bedbankItems.find(item => isActiveItem(item) && !!item.checkIn)
  if (bedbankItem) {
    const imageId = bedbankItem.offer.images?.[0]?.id
    const stayName = bedbankItem.offer.name
    return {
      kind: 'stay',
      startDate: bedbankItem.checkIn,
      endDate: bedbankItem.checkOut,
      duration: bedbankItem.duration,
      stayOfferId: bedbankItem.offer.id,
      ...(imageId ? { imageId } : {}),
      ...(stayName ? { stayName } : {}),
    }
  }

  const tourItem = order.tourItems.find(isActiveItem)
  const tour = tourItem?.tour
  if (tourItem) {
    return {
      kind: 'tour',
      startDate: tour?.startDate,
      endDate: tour?.endDate,
      tourReservationId: tourItem.reservationId,
      imageId: tour?.images?.[0]?.id,
    }
  }

  const cruise = order.cruiseItems.find(isActiveItem)
  if (cruise) {
    return {
      kind: 'cruise',
      startDate: cruise.departureDate,
      endDate: cruise.arrivalDate,
      cruiseBookingId: cruise.bookingId,
    }
  }

  const flightItem = order.flightItems.find(isActiveItem)
  if (flightItem) {
    return {
      kind: 'flight',
      startDate: flightItem.departureDate,
      endDate: flightItem.arrivalDate,
    }
  }

  const experience = order.experienceItems.find(isActiveItem)
  if (experience) {
    const startDate = experience.date ?? experience.bookByDate
    return {
      kind: 'experience',
      startDate,
      endDate: experience.ticket?.rateEndDate ?? startDate,
      experienceId: experience.experienceId,
    }
  }

  return { kind: 'stay' }
}

export default getUpcomingEscapePrimaryBooking
